import { useEffect, useRef, useState, type FormEvent } from "react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { api, ApiError } from "../lib/api";
import type { ClientRecord } from "../lib/types";

export default function Clients() {
  const [ref, setRef] = useState("");
  const [lastName, setLastName] = useState("");
  const [refMsg, setRefMsg] = useState("");
  const [record, setRecord] = useState<ClientRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const recordRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api
      .get<ClientRecord>("/clients/FD-0412")
      .then(setRecord)
      .catch(() => setRecord(null))
      .finally(() => setLoading(false));
  }, []);

  const onRefChange = (raw: string) => {
    let v = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (v.startsWith("FD")) v = v.slice(2);
    v = v.replace(/\D/g, "").slice(0, 4);
    setRef(v ? "FD-" + v : "");
    if (refMsg) setRefMsg("");
  };

  const onLookup = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^FD-\d{4}$/.test(ref)) {
      setRefMsg("Check the reference — four digits, as printed on your statement.");
      return;
    }
    try {
      const found = await api.get<ClientRecord>(`/clients/${ref}?lastName=${encodeURIComponent(lastName)}`);
      setRecord(found);
      setRefMsg("");
      recordRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      setRefMsg(
        err instanceof ApiError && err.status === 404
          ? "No record matches that reference and name."
          : "Check the reference — four digits, as printed on your statement.",
      );
    }
  };

  return (
    <>
      <SiteHeader variant="solid" active="clients" />
      <main>
        <section className="masthead" data-screen-label="Clients masthead">
          <div className="wrap">
            <p className="eyebrow">Clients</p>
            <h1>A coating is a relationship, not a transaction.</h1>
            <p className="lede">
              Every vehicle the house coats keeps a record: what was applied, when, to what stage, and what it needs
              next. Standing clients are held on the calendar — the date exists before you ask for it.
            </p>
            <form className="lookup" onSubmit={onLookup}>
              <div className="field">
                <label htmlFor="ref">Client reference</label>
                <input id="ref" value={ref} onChange={(e) => onRefChange(e.target.value)} placeholder="FD-0000" autoComplete="off" />
                <div className="msg">{refMsg}</div>
              </div>
              <div className="field">
                <label htmlFor="plate">Registration or last name</label>
                <input id="plate" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="e.g. Reyes" autoComplete="off" />
              </div>
              <button type="submit" className="btn">
                Open my record
              </button>
            </form>
          </div>
        </section>

        {!loading && record && (
          <section className="record" data-screen-label="Client record" ref={recordRef}>
            <div className="wrap">
              <div className="rec-head">
                <div>
                  <p className="eyebrow">Record · {record.ref}</p>
                  <h2>
                    {record.vehicle} · {record.color}
                  </h2>
                </div>
                <span className="mono">Client since {record.clientSince}</span>
              </div>
              <div className="rec-grid">
                <div className="panel panel--signed">
                  <span className="mono" style={{ color: "var(--wg-brass)" }}>
                    Protection on file
                  </span>
                  <h3>{record.coatingStage}</h3>
                  <ul className="spec">
                    <li>
                      <span className="k">Coating applied</span>
                      <span className="v">{record.coatingAppliedDate}</span>
                    </li>
                    <li>
                      <span className="k">Stage</span>
                      <span className="v">{record.coatingStage}</span>
                    </li>
                    <li>
                      <span className="k">Surfaces</span>
                      <span className="v">{record.surfaces}</span>
                    </li>
                    <li>
                      <span className="k">Warranty</span>
                      <span className="v">{record.warranty}</span>
                    </li>
                    <li>
                      <span className="k">Last maintenance</span>
                      <span className="v">{record.lastMaintenance}</span>
                    </li>
                    <li>
                      <span className="k">Programme</span>
                      <span className="v">{record.programme}</span>
                    </li>
                  </ul>
                </div>
                <div className="panel">
                  <span className="mono">Next visit</span>
                  <h3 style={{ marginBottom: "var(--space-7)" }}>Held on the calendar</h3>
                  <div className="next">
                    <span className="date">
                      {record.nextVisitDate}
                      <br />
                      {record.nextVisitWindow}
                    </span>
                    <p>{record.nextVisitNote}</p>
                    <div className="next-actions">
                      <a href="/#enquire" className="btn btn--sm">
                        Confirm
                      </a>
                      <a href="/#enquire" className="btn btn--ghost btn--sm">
                        Move this date
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hist-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Work performed</th>
                      <th>Notes</th>
                      <th className="amt">Invoiced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {record.history.map((h, i) => (
                      <tr key={i}>
                        <td>{h.date}</td>
                        <td>{h.description}</td>
                        <td>{h.notes}</td>
                        <td className="amt">${h.amount.toLocaleString("en-US")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mono" style={{ marginTop: "var(--space-7)" }}>
                Statements and warranty documents are sent by email · no account login required
              </p>
            </div>
          </section>
        )}

        <section className="prog" data-theme="inverse" data-screen-label="Maintenance programmes">
          <div className="wrap">
            <p className="eyebrow">Standing appointments</p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-5)", maxWidth: "22ch" }}>The maintenance programme</h2>
            <p className="lede">
              A coating fails from neglect, not from age. Clients on a standing appointment keep the same slot each
              cycle and pay less than the walk-up rate for the same work.
            </p>
            <div className="plans">
              <div className="plan">
                <span className="mono" style={{ color: "var(--wg-brass-light)" }}>
                  Monthly
                </span>
                <h3>Concours</h3>
                <p className="price">$95 / visit</p>
                <ul>
                  <li>Twelve visits a year, same slot</li>
                  <li>Decontamination wash and dry</li>
                  <li>Interior wipe-down and glass</li>
                  <li>Coating inspected each visit</li>
                  <li>Annual top-up included</li>
                </ul>
                <a href="/#enquire" className="btn">
                  Request this programme
                </a>
              </div>
              <div className="plan plan--held">
                <span className="mono" style={{ color: "var(--wg-brass-light)" }}>
                  Quarterly · most held
                </span>
                <h3>Maintained</h3>
                <p className="price">$110 / visit</p>
                <ul>
                  <li>Four visits a year, same slot</li>
                  <li>Decontamination wash and dry</li>
                  <li>Wheel faces and barrels</li>
                  <li>Coating inspected each visit</li>
                  <li>Top-up at cost when needed</li>
                </ul>
                <a href="/#enquire" className="btn">
                  Request this programme
                </a>
              </div>
              <div className="plan">
                <span className="mono" style={{ color: "var(--wg-brass-light)" }}>
                  Twice yearly
                </span>
                <h3>Seasonal</h3>
                <p className="price">$140 / visit</p>
                <ul>
                  <li>Spring and late autumn</li>
                  <li>Winter salt decontamination</li>
                  <li>Wheel and arch detail</li>
                  <li>Coating inspected each visit</li>
                  <li>Priority on open dates</li>
                </ul>
                <a href="/#enquire" className="btn">
                  Request this programme
                </a>
              </div>
            </div>
            <p className="note">
              Programmes are open to vehicles the house has coated or corrected. No contract and no card on file — a
              visit can be moved or skipped with two days' notice, and the programme can be ended at any time.
            </p>
          </div>
        </section>

        <section data-screen-label="Aftercare">
          <div className="wrap">
            <p className="eyebrow">Aftercare</p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-5)", maxWidth: "24ch" }}>
              Keeping a coating in the condition it left in
            </h2>
            <p className="lede">A coated car is easier to look after than an uncoated one, provided nothing abrasive touches it.</p>
            <div className="care-grid">
              <div className="care">
                <h3>The first week</h3>
                <ol>
                  <li>
                    <strong>Seven days, no wash</strong>The coating continues to cure. Rain is fine; soap and pressure are
                    not.
                  </li>
                  <li>
                    <strong>Avoid sprinklers and hard water</strong>Mineral spotting during cure is the one defect that is
                    difficult to reverse.
                  </li>
                  <li>
                    <strong>Park away from trees where possible</strong>Sap and bird lime are acidic and should be removed
                    with water the same day.
                  </li>
                </ol>
              </div>
              <div className="care">
                <h3>Thereafter</h3>
                <ul>
                  <li>
                    <strong>No tunnel washes, ever</strong>The brushes reintroduce exactly the swirling the correction
                    removed. This voids the warranty.
                  </li>
                  <li>
                    <strong>Two buckets, pH-neutral soap</strong>Top down, one panel at a time, dried with a clean plush
                    towel.
                  </li>
                  <li>
                    <strong>Iron and tar removal twice a year</strong>Done on programme visits. Brake dust bonds to the
                    coating, not the paint.
                  </li>
                  <li>
                    <strong>Call before touching a defect</strong>A scratch reached in the first day is usually
                    correctable. Polished at home, it usually is not.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="closing" data-screen-label="Closing">
          <div className="wrap closing-in">
            <div>
              <p className="eyebrow">Existing clients</p>
              <h2>Move a date, or add a vehicle to the record.</h2>
            </div>
            <div className="acts">
              <a href="tel:+14105550118" className="btn btn--ghost">
                (410) 555-0118
              </a>
              <a href="/#enquire" className="btn">
                Request an appointment
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter variant="clients" />
    </>
  );
}
